import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, inr } from "@/lib/api";
import InquiryModal from "@/components/InquiryModal";
import { Clock, MapPin, Calendar, Check, X, ChevronDown, ArrowLeft } from "lucide-react";

export default function TourDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [inquiry, setInquiry] = useState(false);
  const [openDay, setOpenDay] = useState(1);

  useEffect(() => {
    api.get(`/tours/${id}`).then((r) => setTour(r.data)).catch(() => setTour(false));
  }, [id]);

  useEffect(() => {
    if (!tour || tour === false) return undefined;
    if (tour.slug && id !== tour.slug) {
      navigate(`/tours/${tour.slug}`, { replace: true });
      return undefined;
    }
    const prevTitle = document.title;
    document.title = tour.metaTitle || `${tour.title} | Global Compass Tours & Travels`;
    const desc = (tour.metaDescription && tour.metaDescription.trim()) || (tour.overview || "").slice(0, 160);
    const ogImg = tour.ogImage ? fileUrl(tour.ogImage) : "https://www.globalcompass.in/logo.jpg";
    const canonical = `https://www.globalcompass.in/tours/${tour.slug || tour.id}`;
    const restores = [];
    const apply = (tagName, idAttr, idVal, contentAttr, contentVal) => {
      let el = document.head.querySelector(`${tagName}[${idAttr}="${idVal}"]`);
      if (el) {
        const prev = el.getAttribute(contentAttr);
        restores.push(() => { if (prev === null) el.removeAttribute(contentAttr); else el.setAttribute(contentAttr, prev); });
      } else {
        el = document.createElement(tagName);
        el.setAttribute(idAttr, idVal);
        document.head.appendChild(el);
        restores.push(() => el.remove());
      }
      el.setAttribute(contentAttr, contentVal);
    };
    apply("meta", "name", "description", "content", desc);
    apply("link", "rel", "canonical", "href", canonical);
    apply("meta", "property", "og:title", "content", tour.metaTitle || tour.title);
    apply("meta", "property", "og:description", "content", desc);
    apply("meta", "property", "og:image", "content", ogImg);
    apply("meta", "property", "og:url", "content", canonical);
    return () => { document.title = prevTitle; restores.forEach((r) => r()); };
  }, [tour, id, navigate]);

  if (tour === null) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (tour === false) return <div className="p-20 text-center text-slate-600">Tour not found. <Link to="/tours" className="text-amber-700 underline">Back to tours</Link></div>;

  return (
    <div className="bg-ivory select-none" onContextMenu={(e) => e.preventDefault()} data-testid="tour-detail-page">
      <div className="relative h-[60vh] min-h-[420px] bg-maroon overflow-hidden">
        <img src={fileUrl(tour.coverImage)} alt={tour.title} className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute inset-0 grain pointer-events-none opacity-40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-10 w-full text-white">
            <Link to="/tours" className="flex w-fit items-center gap-2 text-amber-200 hover:text-white mb-5 text-sm font-semibold" data-testid="back-to-tours">
              <ArrowLeft size={16} /> Back to tours
            </Link>
            <div className="mb-4">
              <span className="inline-block bg-amber-500/25 border border-amber-200/40 text-amber-100 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full">{tour.category}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-tight max-w-4xl" data-testid="tour-title">{tour.title}</h1>
            <div className="mt-4 flex flex-wrap gap-5 text-amber-50 text-lg">
              <span className="inline-flex items-center gap-2"><MapPin size={20} /> {tour.region}</span>
              <span className="inline-flex items-center gap-2"><Clock size={20} /> {tour.durationDays} days</span>
              {tour.departureDates?.length > 0 && (
                <span className="inline-flex items-center gap-2"><Calendar size={20} /> {tour.departureDates.length} departures</span>
              )}
            </div>
            {tour.types?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="tour-detail-types">
                {tour.types.map((tp) => (
                  <span key={tp} className="text-xs font-semibold bg-amber-500/25 border border-amber-200/40 text-amber-100 px-3 py-1 rounded-full">{tp}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <h2 className="font-display text-3xl font-bold text-maroon mb-4">Overview</h2>
            <p className="text-slate-700 text-lg leading-relaxed">{tour.overview}</p>
          </section>
          {tour.highlights?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl font-bold text-maroon mb-5">Trip Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {tour.highlights.map((h, i) => (
                  <div key={i} className="flex gap-3 items-start bg-white rounded-lg p-4 border border-amber-900/10" data-testid={`highlight-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0 mt-0.5"><Check size={14} /></span>
                    <span className="text-slate-700 text-[15px]">{h}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {tour.itinerary?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl font-bold text-maroon mb-5">Day-by-Day Tour Itinerary</h2>
              <div className="space-y-3">
                {tour.itinerary.filter((d) => d.active !== false).map((d) => (
                  <div key={d.dayNumber} className="bg-white rounded-xl border border-amber-900/10 overflow-hidden" data-testid={`itinerary-day-${d.dayNumber}`}>
                    <button onClick={() => setOpenDay(openDay === d.dayNumber ? 0 : d.dayNumber)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-amber-50/40">
                      <div className="flex items-center gap-4">
                        <span className="w-11 h-11 rounded-full bg-maroon text-amber-100 font-display font-bold grid place-items-center text-lg">{d.dayNumber}</span>
                        <span className="font-display font-bold text-lg text-maroon">{d.title}</span>
                      </div>
                      <ChevronDown className={`transition-transform text-amber-700 ${openDay === d.dayNumber ? "rotate-180" : ""}`} />
                    </button>
                    {openDay === d.dayNumber && (
                      <div className="px-5 pb-5 pt-1">
                        <div className="flex flex-col sm:flex-row gap-4">
                          {d.image && (
                            <figure className="sm:w-40 shrink-0">
                              <img src={fileUrl(d.image)} alt={d.imageCaption || `Day ${d.dayNumber}: ${d.title} — ${tour.title}`} className="w-full aspect-square object-cover rounded-lg" data-testid={`itinerary-image-${d.dayNumber}`} draggable="false" />
                              {d.imageCaption && (
                                <figcaption className="mt-2 text-sm text-maroon font-medium text-center" data-testid={`itinerary-caption-${d.dayNumber}`}>{d.imageCaption}</figcaption>
                              )}
                            </figure>
                          )}
                          <div className="text-slate-700 text-[15px] leading-relaxed flex-1">{d.description}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="grid sm:grid-cols-2 gap-6">
            {tour.inclusions?.length > 0 && (
              <div className="bg-white rounded-xl border border-amber-900/10 p-6">
                <h3 className="font-display text-xl font-bold text-maroon mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-700 grid place-items-center"><Check size={16} /></span>
                  Inclusions
                </h3>
                <ul className="space-y-2 text-slate-700 text-[15px]">
                  {tour.inclusions.map((h, i) => <li key={i} className="flex gap-2"><Check className="text-green-600 mt-1 shrink-0" size={14} /> {h}</li>)}
                </ul>
              </div>
            )}
            {tour.exclusions?.length > 0 && (
              <div className="bg-white rounded-xl border border-amber-900/10 p-6">
                <h3 className="font-display text-xl font-bold text-maroon mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-red-100 text-red-700 grid place-items-center"><X size={16} /></span>
                  Exclusions
                </h3>
                <ul className="space-y-2 text-slate-700 text-[15px]">
                  {tour.exclusions.map((h, i) => <li key={i} className="flex gap-2"><X className="text-red-600 mt-1 shrink-0" size={14} /> {h}</li>)}
                </ul>
              </div>
            )}
          </section>
          {tour.gallery?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl font-bold text-maroon mb-5">Gallery</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {tour.gallery.map((g, i) => (
                  <img key={i} src={fileUrl(g)} alt={`Gallery ${i+1}`} className="rounded-xl aspect-[4/3] w-full object-cover" />
                ))}
              </div>
            </section>
          )}
          {[["Terms & Conditions","termsConditions"],["Payment Schedule","paymentSchedule"],["Cancellation Policy","cancellationPolicy"]].some(([,k])=>tour[k] && tour[k].trim()) && (
            <section className="space-y-3" data-testid="tour-policies">
              {[["Terms & Conditions","termsConditions"],["Payment Schedule","paymentSchedule"],["Cancellation Policy","cancellationPolicy"]].map(([label,key]) => (
                (tour[key] && tour[key].trim()) ? (
                  <details key={key} className="bg-white rounded-xl border border-amber-900/10 overflow-hidden group" data-testid={`policy-${key}`}>
                    <summary className="flex items-center justify-between p-5 cursor-pointer font-display font-bold text-lg text-maroon list-none">
                      {label}
                      <ChevronDown className="policy-chev text-amber-700" />
                    </summary>
                    <div className="px-5 pb-5 text-slate-700 text-[15px] leading-relaxed whitespace-pre-line">{tour[key]}</div>
                  </details>
                ) : null
              ))}
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-32 h-fit">
          <div className="bg-white rounded-2xl border-2 border-amber-900/10 shadow-lg p-7" data-testid="tour-booking-card">
            <div className="text-xs uppercase tracking-widest font-bold text-amber-800 mb-1">Starting from</div>
            <div className="font-display text-4xl font-extrabold text-maroon">{inr(tour.startingPrice)}</div>
            <div className="text-sm text-slate-500 mb-6">per person, on twin sharing</div>
            {tour.departureDates?.length > 0 ? (
              <div className="mb-6">
                <div className="text-sm font-semibold text-maroon mb-2">Upcoming Departures</div>
                <div className="flex flex-wrap gap-2">
                  {tour.departureDates.map((d) => (
                    <span key={d} className="text-sm bg-amber-100 text-amber-800 font-semibold px-3 py-1.5 rounded-full">
                      {new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-6 text-slate-600 text-sm bg-amber-50 rounded-lg p-3 border border-amber-100">
                Flexible departure — customized around your dates.
              </div>
            )}
            <button onClick={()=>setInquiry(true)} className="btn-gold w-full h-14 rounded-full font-bold text-lg" data-testid="tour-inquire-btn">
              Enquire / Book This Tour
            </button>
            <a href="tel:+919874250100" className="mt-3 block text-center btn-maroon h-14 rounded-full font-bold text-lg leading-[3.5rem]" data-testid="tour-call-btn">
              Call
            </a>
            <div className="mt-5 pt-5 border-t border-amber-900/10 text-sm text-slate-600 space-y-2">
              <div>✓ 16 Years of Trust</div>
              <div>✓ Experienced Tour Manager</div>
              <div>✓ Suitable for All Ages</div>
              <div>✓ Transparent Pricing</div>
              <div>✓ Hassle-Free Travel</div>
            </div>
          </div>
        </aside>
      </div>
      <InquiryModal open={inquiry} onClose={()=>setInquiry(false)} tourId={tour.id} tourTitle={tour.title} tourCategory={tour.category} departureDates={tour.departureDates || []} />
    </div>
  );
}
